/**
 * Integraciones.tsx — lo que Books podrá recibir, y a qué cuentas iría.
 *
 * ── Por qué esta pantalla no es la del mockup ─────────────────────────────
 *
 * El mockup mostraba Skoped Kids conectado, 42 asientos procesados y una
 * llave `sk_live_4f8c21ab90de77c3`. Nada de eso existe, y el requisito no era
 * que existiera:
 *
 *   > «debe funcionar inicialmente sin integraciones, pero diseñado para que
 *   > las pueda tener de manera sencilla»
 *
 * Así que la pantalla muestra el estado que hoy es CIERTO —ninguna conectada—
 * con todo lo que sí es real y sí interesa a quien evalúa el producto: qué
 * conectores vienen, en qué etapa, y **a qué cuentas del catálogo iría cada
 * evento**. Ese mapeo no se inventa: sale del catálogo contable.
 *
 * Y no aparece ninguna llave. Una cadena con forma de credencial en una
 * pantalla de demostración termina copiada a algún lado por alguien que cree
 * que sirve; la pantalla dice por qué no hay ninguna en vez de fabricar una.
 *
 * El estado vacío no es un hueco: es la pantalla del día uno, y es la misma
 * que se verá el día en que empiecen a llegar eventos.
 */
import { useIdioma } from '../nucleo/idioma.tsx';
import type { Clave } from '../nucleo/idioma.ts';
import {
  EVENTOS, INTEGRACIONES, type Integracion,
} from '../datos/integraciones.ts';
import { nombreDe, useCatalogo } from '../nucleo/catalogo.tsx';
import { cuentaQueRecibe } from '../nucleo/buscar.ts';
import { useSesion } from '../nucleo/sesion.tsx';

const MECANISMO: Record<Integracion['mecanismo'], Clave> = {
  api: 'int.mech.api',
  archivo: 'int.mech.archivo',
  servicio: 'int.mech.servicio',
};

const ESTADO: Record<Integracion['estado'], { clave: Clave; clase: string }> = {
  disponible: { clave: 'int.available', clase: 'chip chip--ok' },
  planeada: { clave: 'int.planned', clase: 'chip' },
};

/**
 * ▶ EL MAPEO SALE DEL CATÁLOGO DE LA EMPRESA, NO DE UN FIXTURE.
 *
 * La cabecera de este archivo y la de `datos/integraciones.ts` dicen que «ese
 * mapeo no se inventa: sale del catálogo contable». Salía de `CUENTAS` en
 * `datos/muestra.ts`, o sea que **se inventaba** — con el nombre del catálogo
 * de ejemplo, que puede no ser el que este cliente le puso a esa cuenta.
 *
 * Un contador que renombró la 4110 y ve acá el nombre viejo no descubre un
 * detalle cosmético: descubre que la pantalla le está hablando de otro
 * catálogo. Ahora sale del suyo, y si el código no está en su catálogo se
 * muestra el código a secas en vez de un nombre prestado.
 */
function Tarjeta({ i }: { i: Integracion }) {
  const { t, idioma } = useIdioma();
  const { cuentas } = useCatalogo();
  const estado = ESTADO[i.estado];

  /* ▶▶ Y SE BUSCA LA QUE RECIBE, NO LA QUE SE LLAMA ASÍ.
     Esto decía `cuentas.find((x) => x.codigo === codigo)`, igualdad exacta. Con
     el tercer nivel (`0093`) la `1110` sigue EXISTIENDO —como cuenta de
     mayor— así que la encontraba y la enseñaba: le nombraba al cliente una
     cuenta contra la que no puede registrar nada, y con toda naturalidad.
     El código escrito en `datos/integraciones.ts` es un prefijo. */
  const nombreDeCuenta = (codigo: string): string => {
    const c = cuentaQueRecibe(cuentas, codigo);
    return c ? `${c.codigo} · ${nombreDe(c, idioma)}` : codigo;
  };

  return (
    <article className="integ off" style={{ ['--src' as string]: i.color }}>
      <div className="integ__top">
        <span className="integ__mark" style={{ background: i.color }}>{i.marca}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="integ__name">
            {idioma === 'en' && i.nombreEn ? i.nombreEn : i.nombre}
          </div>
          <div className="integ__desc">
            {idioma === 'en' ? i.descripcionEn : i.descripcion}
          </div>
        </div>
        <span className={estado.clase}>{t(estado.clave)}</span>
      </div>

      <div className="integ__body">
        {/* El mapeo es la parte real, y la que de verdad se pregunta. */}
        <div className="label"><span>{t('int.mapsTo')}</span></div>
        {i.mapeo ? (
          <div className="map">
            <span className="small mono">{nombreDeCuenta(i.mapeo.debe)}</span>
            <span className="map__arrow">→</span>
            <span className="small mono">{nombreDeCuenta(i.mapeo.haber)}</span>
          </div>
        ) : (
          <p className="small muted" style={{ margin: 'var(--s4) 0 0' }}>{t('int.noMapping')}</p>
        )}

        <div className="row row--tight" style={{ marginTop: 'var(--s6)' }}>
          <span className="label" style={{ marginBottom: 0 }}>
            <span>{t('int.eventCurrency')}</span>
          </span>
          <span className={`cur cur--${i.moneda === 'USD' ? 'usd' : 'crc'}`}>{i.moneda}</span>
          <span className="spacer" />
          <span className="small muted mono">
            {i.etapa === '—' ? t('wip.noDate') : `${t('int.stage')} ${i.etapa}`}
          </span>
        </div>
      </div>

      {/* Cómo entra el dato. Una importación de archivo NO tiene llave de API,
          y mostrarle una a quien va a subir un CSV le hace buscar algo que no
          existe. */}
      <div className="integ__stats" style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className="stat__k">{t('int.mech')}</div>
          <div className="small muted" style={{ marginTop: 3 }}>{t(MECANISMO[i.mecanismo])}</div>
        </div>
      </div>
    </article>
  );
}

export function Integraciones() {
  const { t } = useIdioma();
  const { sesion } = useSesion();

  // No todas sirven a todos los inquilinos. Las facturas que el hub emite por
  // Skoped Labs solo le importan a los libros de SkopedLabs; ofrecérselas a
  // otro cliente sería ofrecerle algo que no puede tener.
  //
  // ▶ El código sale de la SESIÓN. Comparaba contra `EMPRESA.codigo` de los
  // datos de muestra —`'skopedlabs'` escrito a mano—, así que la integración
  // interna se le ofrecía a TODOS los clientes por igual, que es exactamente lo
  // que este filtro existe para impedir.
  const disponibles = INTEGRACIONES.filter(
    (i) => i.soloPara === undefined || i.soloPara === sesion?.empresa.codigo);

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>{t('int.title')}</h1>
          <p className="page-head__sub">{t('int.subReal')}</p>
        </div>
        <div className="page-actions">
          <span className="chip">{t('int.none')}</span>
          <button className="btn btn--secondary" type="button">{t('int.docs')}</button>
        </div>
      </div>

      <div className="alert alert--info" style={{ marginBottom: 'var(--s7)' }}>
        <span>{t('int.plan')}</span>
      </div>

      <section className="grid grid--2" style={{ marginBottom: 'var(--s7)' }}>
        {disponibles.map((i) => <Tarjeta key={i.clave} i={i} />)}
      </section>

      {/* Por qué un asiento a mano no es de segunda. */}
      <div className="alert alert--ok" style={{ marginBottom: 'var(--s7)' }}>
        <span>{t('int.manualNote')}</span>
      </div>

      <section className="card card--pad0">
        <div className="card__head">
          <h3>{t('int.log')}</h3>
          <span className="spacer" />
          <span className="small muted">
            {t('int.eventCount', { eventos: String(EVENTOS.length) })}
          </span>
        </div>

        {EVENTOS.length === 0 ? (
          <div style={{ padding: 'var(--s10) var(--s7)', textAlign: 'center' }}>
            <p style={{ fontWeight: 700, color: 'var(--heading)', marginBottom: 'var(--s4)' }}>
              {t('int.logEmpty')}
            </p>
            <p className="small muted" style={{ maxWidth: '54ch', margin: '0 auto' }}>
              {t('int.logEmptyBody')}
            </p>
          </div>
        ) : (
          <div className="log">
            {EVENTOS.map((e) => (
              <div key={e.id} className="log__row">
                <span className="log__t">{e.cuando}</span>
                <span>{e.descripcion}</span>
                <span className={e.resultado === 'procesado' ? 'chip chip--ok' : 'chip chip--danger'}>
                  {t(e.resultado === 'procesado' ? 'int.processed' : 'int.withError')}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="row" style={{ padding: 'var(--s5) var(--s7)', borderTop: '1px solid var(--border)' }}>
          <span className="small muted">{t('int.retention')}</span>
          <span className="spacer" />
          {/* El plazo es una pregunta abierta de la suite. No se fija acá. */}
          <span className="small muted">{t('int.retentionPending')}</span>
        </div>
      </section>

      <p className="xs muted" style={{ marginTop: 'var(--s6)', maxWidth: '70ch' }}>
        {t('int.keyWhy')}
      </p>
    </>
  );
}
